import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Edit, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { smtpService } from './smtp.service';
import type { SmtpConfig } from './smtp.dto';
import { formatDate } from '@/helpers';

import { useToast } from '../../contexts/ToastContext';
const encryptionOptions = [
  { label: 'None', value: 'none' },
  { label: 'SSL', value: 'ssl' },
  { label: 'TLS', value: 'tls' },
];

const EditSmtp = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();

  const [formData, setFormData] = useState({
    host: '',
    port: '',
    username: '',
    password: '',
    fromEmail: '',
    encryption: 'none',
    status: true,
    updatedAt: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    smtpService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;
      try {
        setLoading(true);
        const config = await smtpService.getSmtpConfig(token, parseInt(id, 10));
        setFormData({
          host: config.host || '',
          port: String(config.port ?? ''),
          username: config.username || '',
          password: '',
          fromEmail: config.fromEmail || '',
          encryption: (config.encryption as string) || 'none',
          status: config.status !== undefined ? !!config.status : true,
          updatedAt: config.updatedAt || '',
        });
      } catch (err) {
        showError(
          err instanceof Error ? err.message : 'Failed to fetch SMTP config'
        );
        navigate('/smtp');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | boolean = (event.target as HTMLInputElement).value;
      if (field === 'status') {
        value = value === 'true';
      }
      setFormData(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.host.trim()) newErrors.host = 'Host is required';
    if (!formData.port.trim()) newErrors.port = 'Port is required';
    if (formData.port && isNaN(Number(formData.port))) {
      newErrors.port = 'Port must be a number';
    }
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.fromEmail.trim()) {
      newErrors.fromEmail = 'From email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail.trim())) {
      newErrors.fromEmail = 'Enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !token || !id) return;

    setSaving(true);
    try {
      const payload: Partial<SmtpConfig> = {
        host: formData.host,
        port: Number(formData.port),
        username: formData.username,
        fromEmail: formData.fromEmail,
        encryption: formData.encryption as SmtpConfig['encryption'],
        status: formData.status,
      };

      if (formData.password.trim()) {
        payload.password = formData.password;
      }

      await smtpService.updateSmtpConfig(token, parseInt(id, 10), payload);
      showSuccess('SMTP config updated successfully!');
      setTimeout(() => {
        navigate('/smtp', {
          state: { message: 'SMTP updated successfully', severity: 'success' },
        });
      }, 1200);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update SMTP');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/smtp');
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading SMTP config...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/smtp')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit SMTP Config
              </Typography>
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update SMTP connection details used for sending emails.
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  SMTP Metadata
                </Typography>
                {formData.updatedAt && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Updated on
                    </Typography>
                    <Typography variant="h6">
                      {formatDate(formData.updatedAt)}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Connection
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Host"
                        value={formData.host}
                        onChange={handleChange('host')}
                        error={!!errors.host}
                        helperText={errors.host}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Port"
                        value={formData.port}
                        onChange={handleChange('port')}
                        error={!!errors.port}
                        helperText={errors.port}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={formData.username}
                        onChange={handleChange('username')}
                        error={!!errors.username}
                        helperText={errors.username}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label="Password"
                        value={formData.password}
                        onChange={handleChange('password')}
                        helperText="Leave blank to keep existing password"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="From Email"
                        value={formData.fromEmail}
                        onChange={handleChange('fromEmail')}
                        error={!!errors.fromEmail}
                        helperText={errors.fromEmail}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Select
                        fullWidth
                        value={String(formData.encryption)}
                        onChange={handleChange('encryption')}
                        displayEmpty
                      >
                        {encryptionOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    {/* <Grid item xs={12} sm={6}>
                      <Select
                        fullWidth
                        value={String(formData.status)}
                        onChange={handleChange('status')}
                      >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                      </Select>
                    </Grid> */}
                  </Grid>

                  <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button variant="outlined" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={saving}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default EditSmtp;
